import { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Input,
  Modal,
  Select,
  useToast,
} from '@adop/ui';
import { Plus, Users } from 'lucide-react';
import { ROLES } from '@adop/shared';
import type { Role } from '@adop/shared';
import { useAuth } from '../../lib/auth-context';
import { useWorkspace } from '../../lib/workspace-context';
import { useAsync } from '../../lib/useAsync';
import {
  listMemberships,
  listProfilesByIds,
  removeMembership,
  updateMembership,
  type MembershipRow,
  type ProfileRow,
} from '../../lib/data';
import { postJson, errorMessage } from '../../lib/api';
import { humanize } from '../../lib/format';
import { isLastOwner } from '../../lib/team';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { RoleBadge } from '../../components/StatusBadge';
import { ListSkeleton } from '../../components/LoadingState';

interface TeamData {
  memberships: MembershipRow[];
  profiles: Record<string, ProfileRow>;
}

export function TeamPage() {
  const { restaurant, canOwn, visibleLocations } = useWorkspace();
  const { user } = useAuth();
  const { toast } = useToast();
  const state = useAsync<TeamData | null>(async () => {
    if (!restaurant) return null;
    const memberships = await listMemberships(restaurant.id);
    const profiles = await listProfilesByIds(memberships.map((membership) => membership.user_id));
    return {
      memberships,
      profiles: Object.fromEntries(profiles.map((profile) => [profile.id, profile])),
    };
  }, [restaurant?.id]);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('staff');
  const [inviteAllLocations, setInviteAllLocations] = useState(true);
  const [inviteLocations, setInviteLocations] = useState<string[]>([]);
  const [editTarget, setEditTarget] = useState<MembershipRow | null>(null);
  const [editRole, setEditRole] = useState<Role>('staff');
  const [editAllLocations, setEditAllLocations] = useState(true);
  const [editLocations, setEditLocations] = useState<string[]>([]);
  const [removeTarget, setRemoveTarget] = useState<MembershipRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const editingLastOwnerSelf =
    editTarget !== null &&
    editTarget.user_id === user?.id &&
    isLastOwner(state.data?.memberships ?? [], editTarget.user_id);

  if (!restaurant) {
    return <EmptyState icon={<Users className="h-6 w-6" />} title="No restaurant selected" />;
  }

  const sendInvite = async () => {
    setBusy(true);
    setError(null);
    try {
      await postJson('/api/platform/invites', {
        restaurant_id: restaurant.id,
        email: inviteEmail.trim(),
        role: inviteRole,
        location_ids: inviteAllLocations ? null : inviteLocations,
      });
      toast({ title: 'Invitation sent', variant: 'success' });
      setInviteOpen(false);
      setInviteEmail('');
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    setBusy(true);
    setError(null);
    try {
      await updateMembership(restaurant.id, editTarget.user_id, {
        role: editRole,
        location_ids: editAllLocations ? null : editLocations,
      });
      toast({ title: 'Member updated', variant: 'success' });
      setEditTarget(null);
      state.reload();
    } catch (cause) {
      toast({ title: 'Could not update member', description: errorMessage(cause), variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async () => {
    if (!removeTarget) return;
    try {
      await removeMembership(restaurant.id, removeTarget.user_id);
      toast({ title: 'Member removed', variant: 'success' });
      setRemoveTarget(null);
      state.reload();
    } catch (cause) {
      toast({ title: 'Could not remove member', description: errorMessage(cause), variant: 'error' });
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Team"
        description="Who can access this restaurant and what they can do."
        action={
          canOwn ? (
            <Button size="sm" onClick={() => setInviteOpen(true)}>
              <Plus className="h-4 w-4" /> Invite teammate
            </Button>
          ) : null
        }
      />

      {error ? <Alert>{error}</Alert> : null}
      {!canOwn ? (
        <Alert variant="info">Only the owner can invite or change team members.</Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
        </CardHeader>
        <CardContent>
          {state.loading ? (
            <ListSkeleton rows={2} />
          ) : (
            <div className="space-y-2">
              {(state.data?.memberships ?? []).map((membership) => {
                const profile = state.data?.profiles[membership.user_id];
                const restricted = membership.location_ids;
                return (
                  <div
                    key={membership.user_id}
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-200 p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-zinc-900">
                        {profile?.full_name ?? 'Teammate'}
                        {membership.user_id === user?.id ? ' (you)' : ''}
                      </p>
                      <p className="text-xs text-zinc-500">
                        {restricted && restricted.length > 0
                          ? `${restricted.length} location${restricted.length === 1 ? '' : 's'}`
                          : 'All locations'}
                      </p>
                    </div>
                    <RoleBadge role={membership.role} />
                    {canOwn ? (
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditTarget(membership);
                            setEditRole(membership.role as Role);
                            setEditAllLocations(!membership.location_ids);
                            setEditLocations(membership.location_ids ?? []);
                          }}
                        >
                          Edit
                        </Button>
                        {membership.user_id !== user?.id ? (
                          <Button variant="ghost" size="sm" onClick={() => setRemoveTarget(membership)}>
                            Remove
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Modal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite teammate"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button loading={busy} disabled={!inviteEmail.trim()} onClick={() => void sendInvite()}>
              Send invite
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Email"
            type="email"
            value={inviteEmail}
            onChange={(event) => setInviteEmail(event.target.value)}
          />
          <Select
            label="Role"
            value={inviteRole}
            onChange={(event) => setInviteRole(event.target.value as Role)}
          >
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {humanize(role)}
              </option>
            ))}
          </Select>
          <Checkbox
            label="Access all locations"
            checked={inviteAllLocations}
            onChange={(event) => setInviteAllLocations(event.target.checked)}
          />
          {!inviteAllLocations ? (
            <div className="space-y-1">
              {visibleLocations.map((location) => (
                <Checkbox
                  key={location.id}
                  label={location.name}
                  checked={inviteLocations.includes(location.id)}
                  onChange={(event) =>
                    setInviteLocations((current) =>
                      event.target.checked
                        ? [...current, location.id]
                        : current.filter((id) => id !== location.id),
                    )
                  }
                />
              ))}
            </div>
          ) : null}
        </div>
      </Modal>

      <Modal
        open={editTarget !== null}
        onClose={() => setEditTarget(null)}
        title="Edit member"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setEditTarget(null)}>
              Cancel
            </Button>
            <Button loading={busy} onClick={() => void saveEdit()}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            label="Role"
            value={editRole}
            disabled={editingLastOwnerSelf}
            hint={
              editingLastOwnerSelf
                ? 'You are the only owner. Promote another member to owner before changing your role.'
                : undefined
            }
            onChange={(event) => setEditRole(event.target.value as Role)}
          >
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {humanize(role)}
              </option>
            ))}
          </Select>
          <Checkbox
            label="Access all locations"
            checked={editAllLocations}
            onChange={(event) => setEditAllLocations(event.target.checked)}
          />
          {!editAllLocations ? (
            <div className="space-y-1">
              {visibleLocations.map((location) => (
                <Checkbox
                  key={location.id}
                  label={location.name}
                  checked={editLocations.includes(location.id)}
                  onChange={(event) =>
                    setEditLocations((current) =>
                      event.target.checked
                        ? [...current, location.id]
                        : current.filter((id) => id !== location.id),
                    )
                  }
                />
              ))}
            </div>
          ) : null}
        </div>
      </Modal>

      <ConfirmDialog
        open={removeTarget !== null}
        title="Remove this member?"
        message="They will lose access to this restaurant immediately."
        confirmLabel="Remove"
        variant="danger"
        onConfirm={() => void removeMember()}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
}
